// HorizontalAverageEnergyBarChart.jsx
// react
import { useMemo } from 'react';

// material ui
import { Card, useTheme } from '@mui/material';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const toNumOrNull = (v) => (v == null || v === '' ? null : Number(v));
const fmt1 = (v) => (Number.isFinite(v) ? v.toFixed(1) : '—');

export default function HorizontalAverageEnergyBarChart({
  selected,
  energySavings,
  title = 'Average Energy Usage (Daily)',
  height = 240,
  unit,
  categoryLabel,
  mode, // optional override: 'light' | 'dark'
}) {
  const muiTheme = useTheme();
  const isDark = (mode ?? muiTheme.palette.mode) === 'dark';

  // palette tokens (inline theme mapping)
  const colors = useMemo(() => {
    const text = isDark
      ? muiTheme.palette.grey[100]
      : muiTheme.palette.text.primary;
    const muted = isDark
      ? muiTheme.palette.grey[400]
      : muiTheme.palette.text.secondary;
    const bg = isDark ? muiTheme.palette.grey[800] : '#ffffff';
    const grid = isDark
      ? muiTheme.palette.grey[800]
      : muiTheme.palette.grey[300];
    const axis = isDark
      ? muiTheme.palette.grey[700]
      : muiTheme.palette.grey[400];
    const tooltipBg = isDark ? muiTheme.palette.grey[900] : '#ffffff';
    const tooltipBorder = isDark
      ? muiTheme.palette.grey[700]
      : muiTheme.palette.grey[300];
    const tooltipText = text;

    // series colors aligned with your scheme
    const baseline = muiTheme.palette.error.main || '#C62828';
    const control = '#96b100';

    return {
      text,
      muted,
      bg,
      grid,
      axis,
      tooltipBg,
      tooltipBorder,
      tooltipText,
      baseline,
      control,
    };
  }, [isDark, muiTheme]);

  const es = selected?.energySavings ?? energySavings ?? null;
  const baseline = toNumOrNull(es?.AverageEnergyUsageBaselineDay);
  const control = toNumOrNull(es?.AverageEnergyUsageControlDay);
  const resolvedUnit = unit ?? es?.BaseUnit ?? 'kWh';
  const label =
    categoryLabel ?? selected?.Name ?? selected?.BillingMeterId ?? '#1';

  const options = useMemo(
    () => ({
      chart: {
        type: 'bar',
        height,
        backgroundColor: colors.bg,
        style: { fontFamily: muiTheme.typography.fontFamily },
      },
      title: { text: title, style: { color: colors.text, fontWeight: 600 } },
      credits: { enabled: false },
      legend: {
        enabled: true,
        itemStyle: { color: colors.text },
        itemHoverStyle: { color: colors.muted },
      },

      xAxis: {
        categories: [label],
        title: { text: null },
        tickLength: 0,
        lineColor: colors.axis,
        tickColor: colors.axis,
        labels: { style: { color: colors.text } },
      },

      yAxis: {
        title: { text: resolvedUnit, style: { color: colors.text } },
        min: 0,
        gridLineDashStyle: 'ShortDot',
        gridLineColor: colors.grid,
        lineColor: colors.axis,
        labels: { style: { color: colors.text } },
      },

      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: colors.tooltipBg,
        borderColor: colors.tooltipBorder,
        style: { color: colors.tooltipText, pointerEvents: 'none' },
        stickOnContact: false, // don't keep it while hovering the tooltip
        hideDelay: 50,
        formatter: function () {
          const base = this.points?.find(
            (p) => p.series?.name === 'Baseline (intet indgreb)',
          )?.y;
          const ctrl = this.points?.find(
            (p) => p.series?.name === 'Aktiv styring',
          )?.y;
          return `
            <div style="min-width:220px">
              <div style="font-weight:600;margin-bottom:4px">${this.x}</div>
              <div> <span style="
                  display:inline-block;
                  width:10px;height:10px;border-radius:50%;
                  background:${colors.baseline};
                  flex:0 0 auto;
                "></span> Baseline (intet indgreb): <b>${fmt1(base)} ${resolvedUnit}</b></div>
              <div><span style="
                  display:inline-block;
                  width:10px;height:10px;border-radius:50%;
                  background:${colors.control};
                  flex:0 0 auto;
                "></span> Aktiv styring: <b>${fmt1(ctrl)} ${resolvedUnit}</b></div>
            </div>
          `;
        },
      },

      plotOptions: {
        series: {
          borderWidth: 0,
          animation: { duration: 250 },
          pointPadding: 0.1,
          groupPadding: 0.12,
          dataLabels: { enabled: false },
        },
      },

      series: [
        {
          name: 'Baseline (intet indgreb)',
          data: [baseline],
          color: colors.baseline,
        },
        { name: 'Aktiv styring', data: [control], color: colors.control },
      ],
    }),
    [
      baseline,
      control,
      resolvedUnit,
      label,
      title,
      height,
      colors,
      muiTheme.typography.fontFamily,
    ],
  );

  return (
    <Card sx={{ p: 2 }}>
      <div>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </Card>
  );
}

